import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { apiPost, getApiBase } from '../../services/api';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const COOLDOWN_SECONDS = 60;

export default function ForgotPassword() {
  const apiBase = getApiBase();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [emailFormatError, setEmailFormatError] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = window.setTimeout(() => {
      setCooldown((prev) => prev - 1);
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    setEmailFormatError(false);

    const trimmed = email.trim();
    if (!trimmed || !EMAIL_REGEX.test(trimmed)) {
      setEmailFormatError(true);
      return;
    }

    if (!apiBase) {
      setError('Password reset is unavailable.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await apiPost('/api/v1/auth/password/forgot', { email: trimmed });
      const data = await res.json().catch(() => ({}));

      if (res.status === 429) {
        setError('Too many attempts. Please wait before trying again.');
        return;
      }
      if (res.status === 422 && data.errors) {
        setFieldErrors(data.errors);
        return;
      }
      if (!res.ok) {
        setError(data.message || 'Something went wrong. Please try again.');
        return;
      }

      setSuccess(true);
      setCooldown(COOLDOWN_SECONDS);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="rwm-auth rwm-forgot-password bg-white">
      <div className="rwm-auth__card py-5">
        <Card className="rwm-forgot-password__card border" style={{ borderColor: 'var(--rwm-border)' }}>
          <Card.Body className="p-4">
            <h1 className="h4 fw-semibold mb-2 rwm-forgot-password__title" style={{ color: 'var(--rwm-dark)' }}>
              Reset Your Password
            </h1>
            <p className="text-muted mb-4 rwm-forgot-password__subtitle">
              Enter your email and we&apos;ll send you a reset link.
            </p>

            {success ? (
              <Alert variant="success" data-testid="rwm-forgot-success">
                If this email is registered, a reset link has been sent. Check your inbox.
              </Alert>
            ) : (
              <Form data-testid="rwm-forgot-form" onSubmit={handleSubmit} noValidate>
                <Form.Group className="mb-3" controlId="forgotEmail">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={email}
                    onChange={(ev) => {
                      setEmail(ev.target.value);
                      setEmailFormatError(false);
                    }}
                    required
                    autoComplete="email"
                    data-testid="rwm-forgot-email"
                  />
                  {fieldErrors.email ? (
                    <div className="rwm-auth__field-error mt-1">{fieldErrors.email[0]}</div>
                  ) : emailFormatError ? (
                    <div className="rwm-auth__field-error mt-1">
                      Please enter a valid email address.
                    </div>
                  ) : null}
                </Form.Group>

                {error ? (
                  <p className="rwm-auth__field-error mb-3" data-testid="rwm-forgot-error">
                    {error}
                  </p>
                ) : null}

                <Button
                  type="submit"
                  variant="info"
                  className="w-100 text-white btn-lg"
                  disabled={submitting || cooldown > 0}
                  data-testid="rwm-forgot-submit"
                >
                  {submitting
                    ? 'Sending…'
                    : cooldown > 0
                      ? `Resend in ${cooldown}s…`
                      : 'Send Reset Link'}
                </Button>
              </Form>
            )}

            <p className="small text-center mt-3 mb-0">
              <Link to="/login" className="rwm-job-detail__browse-link text-decoration-none">
                Back to Login
              </Link>
            </p>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
}
